import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChatCircleText, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApi, errText } from "@/lib/api";
import { useCanEdit } from "@/components/app/TrialBanner";

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const useNoteCounts = () => {
  const api = useApi();
  return useQuery({ queryKey: ["scenario-note-counts"], queryFn: () => api.get("/scenarios/notes/counts") }).data || {};
};

const Thread = ({ scenario }) => {
  const api = useApi();
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const key = ["scenario-notes", scenario.id];
  const { data: notes = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/scenarios/${scenario.id}/notes`) });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["scenario-note-counts"] }); };
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/scenarios/${scenario.id}/notes`, { text }); setText(""); refresh(); }
    catch (err) { toast.error(errText(err, "Couldn't add the comment")); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/scenarios/${scenario.id}/notes/${id}`); refresh(); }
    catch (err) { toast.error(errText(err, "Couldn't delete the comment")); }
  };
  return (
    <>
      <div data-testid="scenario-notes-list" className="max-h-80 overflow-y-auto space-y-3 pr-1">
        {notes.length === 0 && <p data-testid="scenario-notes-empty" className="text-sm text-[#6B6B6B]">No comments yet.</p>}
        {notes.map((n, i) => (
          <div key={n.id} data-testid={`scenario-note-${i}`} className={`rounded-lg border p-3 ${n.mine ? "border-[#9D00FF]/40 bg-[#9D00FF]/[0.06]" : "border-[#2A2A2A]"}`}>
            <div className="flex items-center gap-2 text-[11px] text-[#6B6B6B]">
              <span className="truncate text-[#A3A3A3]">{n.mine ? "You" : n.author}</span><span>· {when(n.created_at)}</span>
              {n.can_delete && <button data-testid={`scenario-note-delete-${i}`} onClick={() => remove(n.id)} className="ml-auto hover:text-[#FF5C7A]" aria-label="Delete comment"><Trash size={12} /></button>}
            </div>
            <p className="mt-1 text-sm whitespace-pre-wrap break-words">{n.text}</p>
          </div>
        ))}
      </div>
      {canEdit ? (
        <form onSubmit={add} className="space-y-2">
          <textarea data-testid="scenario-note-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} placeholder="Share your view before the founder decides…"
            className="w-full rounded-lg bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9D00FF]" />
          <button data-testid="scenario-note-submit" disabled={busy || !text.trim()} className="rounded-full bg-[#9D00FF] px-5 py-2 text-sm font-semibold text-white disabled:opacity-40">{busy ? "Posting…" : "Post comment"}</button>
        </form>
      ) : <p data-testid="scenario-notes-viewonly" className="text-xs text-[#6B6B6B]">View-only access: you can read comments but not add them.</p>}
    </>
  );
};

export const ScenarioNotes = ({ scenario, count = 0 }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={`scenario-notes-btn-${scenario.id}`} onClick={() => setOpen(true)} title="Comments"
        className="flex items-center gap-1 text-[11px] text-[#6B6B6B] hover:text-[#D9A6FF] transition-colors">
        <ChatCircleText size={14} />{count > 0 && <span data-testid={`scenario-notes-count-${scenario.id}`}>{count}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="scenario-notes-dialog" className="max-w-lg bg-[#121212] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="truncate">Comments on “{scenario.label}”</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Teammates can weigh in before the founder decides.</DialogDescription>
          </DialogHeader>
          {open && <Thread scenario={scenario} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
